<template>
  <form class="flex flex-col gap-y-4" @submit.prevent="resetPassword">
    <h2>{{ $t("resetPassword.title") }}</h2>
    <div class="py-12 flex flex-col gap-y-6">
      <InputField
        v-model="passwords.password"
        required
        :error="errors?.password"
        type="password"
        :label="$t('resetPassword.passwordLabel')"
      />
      <InputField
        v-model="passwords.passwordRepeat"
        type="password"
        required
        :error="errors?.passwordRepeat"
        :label="$t('resetPassword.passwordRepeatLabel')"
      />
    </div>
    <div class="flex gap-x-4 mt-3">
      <custom-button type="submit" variant="primary" class="flex-1" :busy="loading">
        {{ $t("resetPassword.buttonText") }}
      </custom-button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import { useFetchAction } from "@hooks/fetch";
import { useRoute, useRouter } from "vue-router";
import InputField from "@components/utils/inputs/Input.vue";
import { object, string, ref as yupRef } from "yup";
import { useI18n } from "vue-i18n";
import { useValidation } from "@/hooks/form";
import { useNotify } from "@/components/utils/notifications";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const token = computed(() => route.params.token);

const schema = object({
  password: string().required(t("pages.profile.sections.password.passwordRequired")),
  passwordRepeat: string().oneOf(
    [yupRef("password"), null],
    t("pages.profile.sections.password.passwordMismatch")
  ),
});

const {
  data: passwords,
  errors,
  validatedFn,
} = useValidation(
  schema,
  reactive({
    password: "",
    passwordRepeat: "",
  })
);

const [exec, { loading }] = useFetchAction();

const notify = useNotify();

const resetPassword = validatedFn(async () => {
  try {
    await exec("/users/reset-password", {
      method: "POST",
      body: {
        password: passwords.value.password,
        token: token.value,
      },
    });
    notify?.({
      variant: "success",
      title: t("resetPassword.saveSuccess"),
    });

    await router.push({ name: "login" });
  } catch (e) {
    notify?.({
      variant: "error",
      title: t("resetPassword.saveError"),
    });
  }
});
</script>

<style lang="scss" scoped></style>
